import React from "react"
import { CircularProgress, IconButton, Tooltip } from "@mui/material"
import { ArrowBackIos, ArrowForwardIos, AutoFixHigh, Close, Download, PsychologyAlt, Share } from "@mui/icons-material"
import { ARIA_LABELS, LAYOUT } from "@/shared/components/image-gallery/constants"

interface GalleryControlsProps {
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  onDownload: () => void
  onShare: () => void
  isDownloading: boolean
  isPrevDisabled: boolean
  isNextDisabled: boolean
  showShareButton: boolean
  showDownloadButton: boolean
  closeButtonRef: React.RefObject<HTMLButtonElement | null>
  onRegenerateSame?: () => void
  onRegenerateDifferent?: () => void
  isRegenerating?: boolean
  regenerateDisabledReason?: string | null
}

const GalleryControls: React.FC<GalleryControlsProps> = ({
  onClose,
  onPrev,
  onNext,
  onDownload,
  onShare,
  isDownloading,
  isPrevDisabled,
  isNextDisabled,
  showShareButton,
  showDownloadButton,
  closeButtonRef,
  onRegenerateSame,
  onRegenerateDifferent,
  isRegenerating = false,
  regenerateDisabledReason
}) => {
  const isRegenerateDisabled = Boolean(regenerateDisabledReason) || isRegenerating

  return (
    <>
      <IconButton
        ref={closeButtonRef}
        onClick={onClose}
        size="small"
        aria-label={ARIA_LABELS.close}
        sx={{
          position: "absolute",
          top: 8,
          bgcolor: "#fff",
          right: LAYOUT.closeControlRightOffset,
          color: "gray",
          "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
          zIndex: 1
        }}>
        <Close fontSize="small" />
      </IconButton>

      <IconButton
        onClick={onPrev}
        size="small"
        disabled={isPrevDisabled}
        aria-label={ARIA_LABELS.prev}
        sx={{
          position: "absolute",
          left: 8,
          top: "50%",
          transform: "translateY(-50%)",
          width: LAYOUT.controlButtonSize,
          height: LAYOUT.controlButtonSize,
          bgcolor: "#fff",
          "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
          zIndex: 1
        }}>
        <ArrowBackIos fontSize="small" sx={{ ml: 0.5 }} />
      </IconButton>

      <IconButton
        onClick={onNext}
        size="small"
        disabled={isNextDisabled}
        aria-label={ARIA_LABELS.next}
        sx={{
          width: LAYOUT.controlButtonSize,
          height: LAYOUT.controlButtonSize,
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          color: "gray",
          bgcolor: "#fff",
          "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
          zIndex: 1
        }}>
        <ArrowForwardIos fontSize="small" />
      </IconButton>

      {showDownloadButton && (
        <IconButton
          onClick={onDownload}
          size="small"
          aria-label={ARIA_LABELS.download}
          sx={{
            position: "absolute",
            top: 8,
            right: LAYOUT.downloadControlRightOffset,
            bgcolor: "#fff",
            color: "gray",
            transition: "opacity 0.2s",
            "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            zIndex: 2
          }}>
          {isDownloading ? <CircularProgress size={16} color="inherit" /> : <Download fontSize="small" />}
        </IconButton>
      )}

      {showShareButton && (
        <IconButton
          onClick={onShare}
          size="small"
          aria-label={ARIA_LABELS.share}
          sx={{
            position: "absolute",
            top: 8,
            right: LAYOUT.shareControlRightOffset,
            bgcolor: "#fff",
            color: "gray",
            transition: "opacity 0.2s",
            "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            zIndex: 2
          }}>
          <Share fontSize="small" />
        </IconButton>
      )}

      {onRegenerateDifferent && false && (
        <Tooltip title={regenerateDisabledReason || "Try a different prompt"}>
          <span>
            <IconButton
              onClick={onRegenerateDifferent}
              size="small"
              aria-label={ARIA_LABELS.regenerateDifferent}
              disabled={isRegenerateDisabled}
              sx={{
                position: "absolute",
                top: 8,
                right: LAYOUT.shareControlRightOffset + 40,
                bgcolor: "#fff",
                color: "gray",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                zIndex: 2
              }}>
              <PsychologyAlt fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {onRegenerateSame && false && (
        <Tooltip title={regenerateDisabledReason || "Regenerate this image"}>
          <span>
            <IconButton
              onClick={onRegenerateSame}
              size="small"
              aria-label={ARIA_LABELS.regenerate}
              disabled={isRegenerateDisabled}
              sx={{
                position: "absolute",
                top: 8,
                right: LAYOUT.shareControlRightOffset + (onRegenerateDifferent ? 80 : 40),
                bgcolor: "#fff",
                color: "gray",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                zIndex: 2
              }}>
              {isRegenerating ? <CircularProgress size={16} color="inherit" /> : <AutoFixHigh fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  )
}

export default GalleryControls
